import { useCallback } from 'react';
import { Popover } from '@base-ui/react/popover';
import useAnimatedPresence from '../../../hooks/useAnimatedPresence';
import {
  getDropdownWidthPresetClassName,
  resolveDropdownWidthPreset,
} from '../dropdownWidth';
import {
  DEFAULT_FLOATING_DISMISS_GROUP,
  useFloatingSurfaceDismissal,
} from '../floatingSurfaceDismissal';

function joinClasses(...values) {
  return values.filter(Boolean).join(' ');
}

function toCssLength(value) {
  if (value == null) {
    return undefined;
  }

  return typeof value === 'number' ? `${value}px` : value;
}

function resolveMinWidth(minWidth, matchAnchorWidth) {
  const resolvedMinWidth = toCssLength(minWidth);

  if (matchAnchorWidth && resolvedMinWidth) {
    return `max(var(--anchor-width), ${resolvedMinWidth})`;
  }

  if (matchAnchorWidth) {
    return 'var(--anchor-width)';
  }

  return resolvedMinWidth;
}

function assignRef(target, value) {
  if (!target) {
    return;
  }

  if (typeof target === 'function') {
    target(value);
    return;
  }

  target.current = value;
}

function BasePopover({
  open,
  onOpenChange,
  trigger = null,
  triggerNativeButton = true,
  children,
  anchor = null,
  side = 'bottom',
  align = 'center',
  sideOffset = 8,
  popupClassName = '',
  positionerClassName = '',
  popupStyle = {},
  contentRef = null,
  portalRoot = null,
  matchAnchorWidth = false,
  minWidth = null,
  maxWidth = null,
  surface = 'plain',
  widthPreset = null,
  dismissGroup = DEFAULT_FLOATING_DISMISS_GROUP,
  dismissContextRef = null,
  initialFocus = false,
  finalFocus = true,
}) {
  const {
    minWidth: popupStyleMinWidth,
    maxWidth: popupStyleMaxWidth,
    ...popupStyleWithoutSize
  } = popupStyle || {};
  const { mounted, stage, ref } = useAnimatedPresence(open);
  const isDropdownSurface = surface === 'dropdown';
  const resolvedWidthPreset = isDropdownSurface
    ? resolveDropdownWidthPreset(widthPreset, 'compact')
    : null;
  const resolvedMinWidth = resolveMinWidth(
    popupStyleMinWidth ?? minWidth,
    matchAnchorWidth,
  );
  const resolvedMaxWidth = toCssLength(
    popupStyleMaxWidth ?? maxWidth,
  );

  const setRefs = useCallback((element) => {
    ref.current = element;
    assignRef(contentRef, element);
  }, [contentRef, ref]);

  const { claimEscape } = useFloatingSurfaceDismissal({
    open,
    dismissGroup,
    onDismiss: () => onOpenChange?.(false),
    surfaceRef: ref,
    contextRef: dismissContextRef || anchor,
  });

  // Base UI's own closer is this popover's only Escape listener, so the shared floating
  // owner gates it here instead of a second listener. When a nested editor already claimed
  // the key, or another open surface is innermost, the close is cancelled and the key keeps
  // propagating to that winner. Callers still receive Base UI's original event details.
  const handleOpenChange = useCallback((nextOpen, eventDetails) => {
    if (!nextOpen && eventDetails?.reason === 'escape-key' && !claimEscape(eventDetails.event)) {
      eventDetails.cancel();
      eventDetails.allowPropagation();
      return;
    }
    onOpenChange?.(nextOpen, eventDetails);
  }, [claimEscape, onOpenChange]);

  return (
    <Popover.Root
      open={open}
      onOpenChange={handleOpenChange}
    >
      {trigger ? (
        <Popover.Trigger
          render={trigger}
          nativeButton={triggerNativeButton}
        />
      ) : null}
      {mounted ? (
        <Popover.Portal keepMounted container={portalRoot || undefined}>
          <Popover.Positioner
            anchor={anchor || undefined}
            side={side}
            align={align}
            sideOffset={sideOffset}
            positionMethod="fixed"
            className={joinClasses('base-popover__positioner', positionerClassName)}
          >
            <Popover.Popup
              ref={setRefs}
              className={joinClasses(
                'base-popover__popup',
                isDropdownSurface && 'ui-floating-surface ui-floating-surface--dropdown',
                resolvedWidthPreset && getDropdownWidthPresetClassName(resolvedWidthPreset),
                popupClassName,
              )}
              data-stage={stage}
              initialFocus={initialFocus}
              finalFocus={finalFocus}
              style={{
                ...popupStyleWithoutSize,
                ...(resolvedMinWidth ? { minWidth: resolvedMinWidth } : {}),
                ...(resolvedMaxWidth ? { maxWidth: resolvedMaxWidth } : {}),
              }}
            >
              {children}
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      ) : null}
    </Popover.Root>
  );
}

export default BasePopover;
